// Lógica de filtrado de la mesa de trabajo de Cobranza.
// Extraída a su propio módulo para mantenerla simple, reutilizable y testeable,
// en lugar de vivir embebida dentro del componente de página.

export type BillingView = 'pending' | 'history';

// Último día del mes para una clave 'YYYY-MM' (p. ej. '2026-10' -> '2026-10-31').
export function lastDayOfMonth(month: string): string {
  if (!month) return '';
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return '';
  // El día 0 del mes siguiente equivale al último día del mes actual.
  const date = new Date(y, m, 0);
  return `${month}-${String(date.getDate()).padStart(2, '0')}`;
}

export interface FilterableInvoice {
  folio: string;
  client_name?: string;
  issue_date: string;
  due_date?: string | null;
  status: string;
  pending_balance: number;
}

export interface BillingFilterOptions {
  view: BillingView;
  search: string;
  status: string; // 'Todos' | 'Pagado' | 'Pendiente' | 'Parcial' | 'Vencido' | 'Borrador'
  startDate: string; // filtro explícito de fecha (desde)
  endDate: string; // filtro explícito de fecha (hasta)
  month: string; // 'YYYY-MM' del selector de mes
}

// Estatus que representan una cuenta por cobrar (con saldo aún abierto).
const OPEN_STATUSES = ['pending', 'overdue', 'parcial'];
const STATUS_LABEL_MAP: Record<string, string> = {
  Borrador: 'draft',
  Pagado: 'paid',
  Pendiente: 'pending',
  Parcial: 'parcial',
  Vencido: 'overdue',
};

export function isOpenInvoice(status: string): boolean {
  return OPEN_STATUSES.includes(status);
}

// Filtra las facturas según la vista activa y los filtros de la barra superior.
//
// - Vista 'pending' (por defecto): mesa de trabajo de cartera. El MES
//   seleccionado se evalúa con la FECHA DE VENCIMIENTO (`due_date`), no con la
//   de emisión: una factura emitida en octubre pero con vencimiento en noviembre
//   aparece al seleccionar noviembre, no octubre. Muestra TODA factura con saldo
//   abierto del mes por vencimiento y, además, TODO saldo pendiente con
//   vencimiento en meses anteriores que siga sin liquidarse. Ninguna cuenta por
//   cobrar queda oculta.
// - Vista 'history': muestra exclusivamente lo ya liquidado (paid). No mezcla
//   pendientes. Si el usuario elige un estatus explícito (p. ej. Borrador), se
//   respeta esa selección para no dejar borradores inaccesibles.
export function filterInvoices<T extends FilterableInvoice>(
  invoices: T[],
  opts: BillingFilterOptions
): T[] {
  const { view, search, status, startDate, endDate, month } = opts;
  const term = search.trim().toLowerCase();
  const monthStart = month ? `${month}-01` : '';
  const monthEnd = month ? lastDayOfMonth(month) : '';

  return invoices.filter((f) => {
    // 1) Búsqueda por folio o nombre de cliente.
    if (term) {
      const match = (f.client_name || '').toLowerCase().includes(term)
        || f.folio.toLowerCase().includes(term);
      if (!match) return false;
    }

    // 2) Filtros explícitos de rango de fechas (barra superior).
    if (startDate && f.issue_date < startDate) return false;
    if (endDate && f.issue_date > endDate) return false;

    // 3) Filtro explícito de estatus (solo cuando el usuario elige uno distinto de "Todos").
    if (status !== 'Todos') {
      const target = STATUS_LABEL_MAP[status];
      if (target && f.status !== target) return false;
    }

    // La ventana del mes se compara contra la fecha de VENCIMIENTO para las
    // cuentas por cobrar (con respaldo en la de emisión si no hubiera due_date).
    const dueDate = f.due_date || f.issue_date;
    const dueInMonth = !monthStart || (dueDate >= monthStart && dueDate <= monthEnd);
    const issuedInMonth = !monthStart || (f.issue_date >= monthStart && f.issue_date <= monthEnd);

    if (view === 'pending') {
      // Solo cuentas por cobrar abiertas.
      if (!isOpenInvoice(f.status)) return false;
      // Del mes seleccionado (por vencimiento) o de meses anteriores que sigan
      // con saldo pendiente (pending/overdue/parcial). Se conservan siempre a
      // la vista para garantizar el seguimiento de la cartera.
      const openFromPast = Number(f.pending_balance) > 0
        && (!monthStart || dueDate < monthStart);
      return dueInMonth || openFromPast;
    }

    // Historial: por defecto solo lo completamente liquidado (paid). Si el
    // usuario eligió un estatus explícito, no se fuerza 'paid'.
    if (status === 'Todos' && f.status !== 'paid') return false;
    if (monthEnd && !issuedInMonth) return false;
    return true;
  });
}